<template>
  <div v-if="providerKey" class="ai-key-toggle">
    <v-checkbox
      :model-value="useOwnKey"
      color="primary"
      density="compact"
      hide-details
      @update:model-value="setUseOwnKey"
    >
      <template #label>
        <span class="ai-key-label">Use my {{ provider.label }} key ({{ providerKey.mask }})</span>
      </template>
    </v-checkbox>
    <div v-if="unusableHint" class="ai-key-hint">{{ unusableHint }}</div>
    <div v-else-if="!useOwnKey" class="ai-key-hint">
      {{ brand.name }}'s key will be used instead.
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useBrand } from '@/composables/useBrand'

const props = defineProps({
  aiVersion: { type: Number, default: null }
})

const store = useStore()
const brand = useBrand()

const PROVIDERS = {
  OPENAI: { key: 'OPENAI', label: 'OpenAI' },
  GEMINI: { key: 'GEMINI', label: 'Gemini' }
}

const provider = computed(() => {
  if (props.aiVersion === null || props.aiVersion === undefined) {
    return null
  }

  if (props.aiVersion < 10) {
    return PROVIDERS.OPENAI
  }

  if (props.aiVersion < 15) {
    return PROVIDERS.GEMINI
  }

  return null
})

const currentUser = computed(() => store.state.user.user)
const providerKey = computed(() => (provider.value
  ? (currentUser.value?.aiProviderKeys || []).find((k) => k.provider === provider.value.key)
  : null))

const isUnusable = computed(() => !!providerKey.value && providerKey.value.status !== 'ACTIVE')

const useOwnKey = computed(() => store.state.workflow.useOwnAiKey === true)
const setUseOwnKey = (value) => store.commit('workflow/setUseOwnAiKey', value === true)

const unusableHint = computed(() => {
  if (!isUnusable.value) {
    return null
  }

  if (providerKey.value.status !== 'QUOTA_EXCEEDED') {
    return 'This key was rejected. Replace it in Account > AI.'
  }

  return useOwnKey.value
    ? `This key had no credits last time. Add credits at ${provider.value.label} or it will fail again.`
    : `This key has no credits left. Add credits at ${provider.value.label}, then turn this back on.`
})

watch(
  [providerKey, () => store.state.workflow.workflow?.id],
  () => {
    if (store.state.workflow.useOwnAiKey !== null) {
      return
    }

    store.commit('workflow/setUseOwnAiKey', providerKey.value ? !isUnusable.value : null)
  },
  { immediate: true }
)
</script>

<style scoped>
.ai-key-toggle {
  margin-top: 2px;
}

.ai-key-toggle :deep(.v-selection-control) {
  --v-selection-control-size: 24px;
  min-height: 24px;
}

.ai-key-toggle :deep(.v-selection-control .v-icon) {
  font-size: 16px;
}

.ai-key-label {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity, 0.6));
}

.ai-key-hint {
  margin-left: 24px;
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity, 0.5));
}
</style>
